/* pdp.js — Sport Schindele product page behaviours layered on top of Dawn's
   product-info / product-form / media-gallery machinery.

   - <pdp-size-picker>  unit tabs that relabel the size grid from the chart JSON
     the picker rendered (EU/UK/US/MP from a `custom.size_chart` metafield,
     EU/UK/US/CM from a `size_chart` metaobject table, or EU/MP derived for
     EU-sized footwear — the picker decides which tabs exist, this code just
     switches between whatever keys the rows carry; `data-base-unit` names the
     column holding the option's own values, defaulting to 'eu' for the two
     older chart shapes). The chosen unit survives the variant-selects
     re-render product-info.js performs on every selection.
   - <pdp-mounting>     moved to `mounting.js` — the quick view renders it too,
     so it must load without the rest of this file.
   - Set picker         keeps the ski-set deep link pointing at the selected
     length. The <pdp-configurator-picker> element it opens is defined in
     `configurator-picker.js`, which the quick view loads too.
   - Buy total          keeps the "· €1,049.90" suffix inside the submit button
     in sync with the selected variant and quantity.
   - Gallery arrows     desktop prev/next over the viewer, driving Dawn's
     media-gallery.setActiveMedia().
   - Subject fit        zooms the viewer and the thumbs onto the photographed
     object (a ski is 16% of its own square canvas); maths in
     `pdp-subject-fit.js`, loaded through the importmap.
   - Accessory rows     variant select updates the row price.
*/

(() => {
  const formatMoney = (cents, currency) => {
    const code = currency || window.Shopify?.currency?.active;
    if (!code) return null;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: code,
      }).format(cents / 100);
    } catch {
      return null;
    }
  };

  /* -------------------------------------------------------------------------
     Size picker — unit tabs + grid relabelling
  ------------------------------------------------------------------------- */

  // Chosen unit per section, module-scoped so it survives the variant-selects
  // DOM swap on every option change.
  const sizeUnitBySection = new Map();

  const UNIT_LABELS = { eu: 'EU', uk: 'UK', us: 'US', mp: 'MP', cm: 'CM' };

  /* A unit the shopper picked by hand outranks the market default and is kept
     for the next product and the next visit (size-unit-preferred decides only
     where nobody has chosen). Storage can throw or be empty (private window,
     blocked site data), so every access is guarded and the page works without
     it: the server already rendered the right default. */
  const SIZE_UNIT_KEY = 'sportSchindele:sizeUnit';

  const storedSizeUnit = () => {
    try {
      return window.localStorage.getItem(SIZE_UNIT_KEY);
    } catch {
      return null;
    }
  };

  const rememberSizeUnit = (unit) => {
    try {
      window.localStorage.setItem(SIZE_UNIT_KEY, unit);
    } catch {
      /* nothing to do: the choice then lasts for this page only */
    }
  };

  if (!customElements.get('pdp-size-picker')) {
    customElements.define(
      'pdp-size-picker',
      class PdpSizePicker extends HTMLElement {
        connectedCallback() {
          try {
            this.chart = JSON.parse(this.querySelector('[data-size-chart]')?.textContent || '{}');
          } catch {
            this.chart = {};
          }

          this.addEventListener('click', (event) => {
            const tab = event.target.closest('[data-unit]');
            if (!tab) return;
            event.preventDefault();
            sizeUnitBySection.set(this.dataset.section, tab.dataset.unit);
            rememberSizeUnit(tab.dataset.unit);
            this.applyUnit(tab.dataset.unit);
          });

          // In order: the unit chosen on this page, the unit chosen earlier (any
          // page, any visit), then what the server rendered — the shopper's market
          // scale, falling back to the option's own. Never a bare 'eu': a UK-based
          // table renders its base tab as data-unit="uk", and defaulting to 'eu'
          // would relabel the grid while the UK tab shows as active.
          this.applyUnit(this.openingUnit());
        }

        // The column holding the option's own values. The legacy metafield and
        // derived charts key that column 'eu' and never set the attribute.
        get baseUnit() {
          return this.dataset.baseUnit || 'eu';
        }

        offers(unit) {
          return Boolean(unit && this.querySelector(`[data-unit="${CSS.escape(unit)}"]`));
        }

        openingUnit() {
          const chosen = sizeUnitBySection.get(this.dataset.section);
          if (this.offers(chosen)) return chosen;
          const remembered = storedSizeUnit();
          if (this.offers(remembered)) return remembered;
          return this.dataset.activeUnit || this.baseUnit;
        }

        convert(value, unit) {
          // No special case for the base unit: table rows carry their base
          // column too, and the two older chart shapes fall through to the raw
          // value because their rows simply have no 'eu' key.
          return this.chart?.[value]?.[unit] ?? value;
        }

        applyUnit(unit) {
          this.querySelectorAll('[data-unit]').forEach((tab) => {
            const active = tab.dataset.unit === unit;
            tab.classList.toggle('is-active', active);
            tab.setAttribute('aria-pressed', String(active));
          });

          const grid = this.querySelector('[data-size-grid]');
          if (grid) {
            grid.querySelectorAll('input[type="radio"]').forEach((input) => {
              const label = grid.querySelector(`label[for="${CSS.escape(input.id)}"]`);
              if (!label) return;
              const textNode = Array.from(label.childNodes).find(
                (node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim() !== ''
              );
              if (textNode) textNode.textContent = this.convert(input.value, unit);
            });
          }

          const note = this.querySelector('[data-size-note]');
          const selected = this.querySelector('[data-size-grid] input:checked');
          if (note && selected) {
            // The base unit is "the option's own values": `data-base-label`
            // names their system ("EU", "UK", "MP (cm)") and is empty when no
            // scale is known — the note then shows the bare value. (`??` would
            // not catch that — an empty string is not nullish — which is why the
            // fallback is `||`.)
            const label = unit === this.baseUnit ? this.dataset.baseLabel || '' : UNIT_LABELS[unit] || unit.toUpperCase();
            note.textContent = [label, this.convert(selected.value, unit)].filter(Boolean).join(' ');
          }
        }
      }
    );
  }

  /* -------------------------------------------------------------------------
     Page-level wiring (buy total, gallery arrows, accessory prices)
  ------------------------------------------------------------------------- */

  const initProductPage = () => {
    const root = document.querySelector("product-info[data-update-url='true']");

    /* The ski-set CTA carries the chosen length into the configurator as
       `?<step>=<variantId>` — the configurator hydrates each step from a variant id
       (assets/bundle-configurator-state.js). The href is rendered server-side from
       the initially selected variant, so it has to follow every length change or the
       customer would arrive with the wrong ski preselected.

       All of them, not one: a ski listed in several configurators renders one link
       per set inside <pdp-configurator-picker> (the element itself is defined in
       `configurator-picker.js`, since the quick view renders the same CTA), each
       with its own step param, and the customer may open the dialog long after
       changing the length. */
    const syncBundleCta = (variant) => {
      if (!variant) return;
      root.querySelectorAll('[data-bundle-cta]').forEach((cta) => {
        const param = cta.dataset.preselectParam;
        if (!param) return;
        const url = new URL(cta.getAttribute('href'), window.location.origin);
        // `handle:id`, never a bare id: the configurator's deferred resolver
        // (collection-backed steps, product past the first page) ignores a
        // bare id and silently drops the ski. The handle rides on the CTA.
        const handle = cta.dataset.preselectHandle;
        url.searchParams.set(param, handle ? `${handle}:${variant.id}` : String(variant.id));
        cta.setAttribute('href', url.pathname + url.search);
      });
    };

    if (!root || root.dataset.pdpInit) return;
    root.dataset.pdpInit = 'true';

    /* --- Way back into the configurator (CFG-12) ---

       The configurator stamps `?konfigurator=<its path + query>` onto every
       product link it hands out (bundle-configurator.js withReturnParam); the
       query is the set state itself. Accept only a same-origin product path —
       optionally locale-prefixed (/en/products/...) — never a host, a scheme or
       a protocol-relative "//": the value comes from the URL bar. Read once at
       init: product-info's own updateURL rewrites the query to `?variant=` on a
       length change, but the link is already filled by then. While the way back
       is on offer the derived set CTA is not — it would restart the set with
       just this ski. */
    const configuratorReturnTarget = () => {
      const raw = new URLSearchParams(window.location.search).get('konfigurator');
      if (!raw || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[^/?#]+(?:[?#]|$)/i.test(raw)) return null;
      const url = new URL(raw, window.location.origin);
      if (url.origin !== window.location.origin) return null;
      return url.pathname + url.search;
    };
    const returnTarget = configuratorReturnTarget();
    if (returnTarget) {
      root.querySelectorAll('[data-configurator-return]').forEach((link) => {
        link.setAttribute('href', returnTarget);
        link.hidden = false;
      });
      root.querySelectorAll('[data-bundle-cta], pdp-configurator-picker').forEach((el) => {
        el.hidden = true;
      });
    }

    /* --- Buy total ("· €1,049.90" inside the submit button) --- */

    const updateTotal = () => {
      const total = root.querySelector('[data-pdp-total]');
      if (!total) return;
      const unitPrice = parseInt(total.dataset.unitPrice, 10);
      if (Number.isNaN(unitPrice)) return;
      const quantity = parseInt(root.querySelector('.quantity__input')?.value, 10) || 1;
      const formatted = formatMoney(unitPrice * quantity, total.dataset.currency);
      if (formatted) total.textContent = `· ${formatted}`;
    };

    root.addEventListener('change', (event) => {
      if (event.target.matches('.quantity__input')) updateTotal();
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.quantityUpdate, updateTotal);
      subscribe(PUB_SUB_EVENTS.variantChange, ({ data: { sectionId, variant } }) => {
        if (sectionId !== root.dataset.section) return;
        const total = root.querySelector('[data-pdp-total]');
        if (total && variant) {
          total.dataset.unitPrice = variant.price;
          updateTotal();
        }
        syncBundleCta(variant);
        window.requestAnimationFrame(() => {
          updateGalleryNav();
          fitVisibleMedia();
        });
      });
    }

    updateTotal();

    /* --- Gallery prev/next arrows --- */

    const gallery = root.querySelector('media-gallery.pdp-media');
    const mediaItems = () =>
      gallery ? Array.from(gallery.querySelectorAll('.product__media-list li[data-media-id]')) : [];

    const updateGalleryNav = () => {
      if (!gallery) return;
      const items = mediaItems();
      const activeIndex = items.findIndex((item) => item.classList.contains('is-active'));
      gallery.querySelectorAll('[data-pdp-media-nav]').forEach((button) => {
        const direction = button.dataset.pdpMediaNav === 'next' ? 1 : -1;
        const target = activeIndex + direction;
        button.toggleAttribute('disabled', target < 0 || target >= items.length || activeIndex === -1);
      });
    };

    // The rail is clipped to the viewer height, so with more media than fit it
    // has to follow the selection. media-gallery.js only knows how to scroll the
    // strip horizontally (`scrollTo({ left })`), which is a no-op here.
    const railFollowActiveThumb = () => {
      const rail = gallery?.querySelector('.thumbnail-list');
      if (!rail || rail.scrollHeight <= rail.clientHeight + 1) return;
      const item = rail.querySelector('button[aria-current]')?.closest('.thumbnail-list__item');
      if (!item) return;
      const top = item.offsetTop;
      const bottom = top + item.offsetHeight;
      if (top < rail.scrollTop) {
        rail.scrollTo({ top, behavior: 'smooth' });
      } else if (bottom > rail.scrollTop + rail.clientHeight) {
        rail.scrollTo({ top: bottom - rail.clientHeight, behavior: 'smooth' });
      }
    };

    if (gallery) {
      gallery.addEventListener('click', (event) => {
        const button = event.target.closest('[data-pdp-media-nav]');
        if (button) {
          const items = mediaItems();
          const activeIndex = items.findIndex((item) => item.classList.contains('is-active'));
          const target = items[activeIndex + (button.dataset.pdpMediaNav === 'next' ? 1 : -1)];
          if (target) gallery.setActiveMedia?.(target.dataset.mediaId, false);
        }
        // Thumbnail clicks also move the active media — refresh either way.
        window.requestAnimationFrame(() => {
          updateGalleryNav();
          railFollowActiveThumb();
          fitVisibleMedia();
        });
      });

      // Swiping the mobile carousel only moves Dawn's active *thumbnail*, so the
      // arrows would keep stepping from wherever the last click left `is-active`.
      // Mirror the scrolled-to slide back onto it. (Harmless on desktop, where
      // `is-active` is also what makes a slide the visible one.)
      gallery.querySelector('[id^="GalleryViewer"]')?.addEventListener('slideChanged', (event) => {
        const mediaId = event.detail?.currentElement?.dataset?.mediaId;
        if (!mediaId) return;
        mediaItems().forEach((item) => item.classList.toggle('is-active', item.dataset.mediaId === mediaId));
        updateGalleryNav();
        railFollowActiveThumb();
        fitVisibleMedia();
      });

      updateGalleryNav();
    }

    /* --- Subject fit: zoom onto the photographed object ---

       The viewer contains the PHOTO in its tile; the photo of a ski is a thin
       object on a square white canvas, so the ski rendered ~95px wide in an
       800px tile and no tile ratio could change that (section-pdp.css explains
       the tile). Here every gallery image gets a 96px copy of its file (the CDN
       allows cross-origin reads), its object's bounding box is measured on a
       canvas, and the image is scaled and shifted with a `transform` so the
       object fills the tile's padded box. A transform does not lay out, so
       nothing shifts; the tile's `overflow: hidden` clips the blown-up canvas.

       The probing, remembering and applying live in pdp-subject-fit.js
       (boxFor, fitPair), because the gallery snippet's inline script runs the
       same code for the photo shown first as soon as the gallery is parsed:
       this deferred script only executes after every other deferred script,
       which in Safari came 600ms+ after the photo was ready, so the photo
       showed unfitted and then snapped (Gero, 22.09.2026, twice). Here the
       module takes over:
       the rest of the media, and every refit — hidden slides have no box to
       measure, so the fit is applied to whatever is visible now and again on
       every activation, variant change, image load and resize.

       The photo is never held back for the decision (it was, from 22.09.2026,
       and cost ~0.4s of blank tile on slow phones, 28.09.2026). A decision
       that lands after the photo has loaded eases in (section-pdp.css,
       "Subject fit"). Everything is best-effort: no importmap
       (Safari <= 16.3), a blocked canvas, a failed fetch — the image simply
       stays as rendered. */

    let subjectFitModule = null;

    const galleryImages = () =>
      gallery
        ? Array.from(
            gallery.querySelectorAll('.product__media-list .media-type-image .product__media img, .thumbnail img')
          )
        : [];

    // The viewer image of a media id is the source of truth for the file URL
    // and its pixel size; the thumb shows the same file smaller.
    const viewerImage = (mediaId) =>
      gallery?.querySelector(`.product__media-list li[data-media-id="${mediaId}"] .media-type-image .product__media img`);

    const fitVisibleMedia = () => {
      if (!gallery || !subjectFitModule) return;
      const fit = subjectFitModule;
      mediaItems().forEach((item) => {
        const mediaId = item.dataset.mediaId;
        const viewer = viewerImage(mediaId);
        if (!viewer) return;
        const thumb = gallery.querySelector(`.thumbnail-list__item[data-target="${mediaId}"] .thumbnail img`);
        // The section id is part of the DOM id; the photo's own id is the key.
        fit.fitPair(viewer, thumb, mediaId.split('-').pop());
      });
    };

    if (gallery && gallery.querySelector('.media-type-image')) {
      import('#pdp-subject-fit')
        .then((module) => {
          subjectFitModule = module;
          fitVisibleMedia();
          // The viewer image's natural size arrives with its bytes; the LCP
          // image is usually still loading when the 96px probe has answered.
          gallery.querySelectorAll('.product__media-list .media-type-image .product__media img').forEach((img) => {
            if (!img.complete) img.addEventListener('load', fitVisibleMedia, { once: true });
          });
          new ResizeObserver(fitVisibleMedia).observe(gallery);
        })
        .catch(() => {
          // No importmap or module: mark every image decided as "none" (the
          // module's releaseImage is not available here); the gallery stays as
          // rendered.
          galleryImages().forEach((img) => {
            img.dataset.subjectFit = 'none';
          });
        });
    }

    /* --- Mobile thumb-strip scroll progress bar --- */

    const thumbStrip = gallery?.querySelector('.thumbnail-list');
    const progress = gallery?.querySelector('[data-pdp-thumb-progress]');
    const progressBar = progress?.querySelector('[data-pdp-thumb-progress-bar]');

    const updateThumbProgress = () => {
      if (!thumbStrip || !progress || !progressBar) return;
      const { scrollWidth, clientWidth, scrollLeft } = thumbStrip;
      const overflow = scrollWidth - clientWidth;
      // The desktop rail scrolls vertically, so there is nothing to report there.
      progress.classList.toggle('pdp-media__progress--inert', overflow <= 1);
      if (overflow <= 1) return;
      const fill = Math.max(24, (clientWidth / scrollWidth) * clientWidth);
      progressBar.style.width = `${fill}px`;
      progressBar.style.transform = `translateX(${(scrollLeft / overflow) * (clientWidth - fill)}px)`;
    };

    if (thumbStrip && progress) {
      thumbStrip.addEventListener('scroll', updateThumbProgress, { passive: true });
      window.addEventListener('resize', updateThumbProgress);
      new ResizeObserver(updateThumbProgress).observe(thumbStrip);
      updateThumbProgress();
    }

    /* --- Accessory rows: variant select drives the price --- */

    root.addEventListener('change', (event) => {
      const select = event.target.closest('[data-accessory-variants]');
      if (!select) return;
      const option = select.selectedOptions[0];
      const price = parseInt(option?.dataset.price, 10);
      const compareAt = parseInt(option?.dataset.compareAtPrice, 10);
      const wrapper = select.closest('.pdp-accessory__form')?.querySelector('[data-accessory-price]');
      if (!wrapper || Number.isNaN(price)) return;

      const formatted = formatMoney(price);
      if (!formatted) return;
      const current = wrapper.querySelector('[data-accessory-price-current]');
      if (current) current.textContent = formatted;

      // Mirror the Liquid rule: strike-through only when compare-at beats the price.
      const onSale = !Number.isNaN(compareAt) && compareAt > price;
      const compare = wrapper.querySelector('[data-accessory-price-compare]');
      if (compare) {
        compare.textContent = onSale ? formatMoney(compareAt) || '' : '';
        compare.hidden = !onSale;
      }
      wrapper.querySelectorAll('[data-accessory-price-sale-label], [data-accessory-price-compare-label]').forEach((label) => {
        label.hidden = !onSale;
      });
      wrapper.classList.toggle('pdp-accessory__price--sale', onSale);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductPage);
  } else {
    initProductPage();
  }
})();
